import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

import { useSocket } from "./data/useSocket";

import { ApiTagType, ModuleConstant, RTKApi } from "@/app/app.types";
import { SocketData, SocketNamespaces } from "@/interfaces/globals";
import { handleSocketUpdate, tableUpdateToast } from "@/utils/helpers";

export interface SocketInvalidationConfig<TApi extends RTKApi = RTKApi> {
  api: TApi;
  tags: ApiTagType[];
  /**
   * Optional module name to enable multi-tab synchronization logic.
   * If provided, the hook will check if the current tab initiated the change.
   */
  moduleName?: ModuleConstant;
}

export interface SocketEventConfig<T = SocketData> {
  /** A single event string or an array of event strings */
  events: string | string[];
  /** Support invalidating multiple APIs or tag sets */
  invalidations?: SocketInvalidationConfig[];
  /** Success toast message to display (can be dynamic based on SocketData) */
  toastMessage?: string | ((data: T) => string);
  /** Optional custom callback to run when the event is received */
  onEvent?: (data: T) => void;
  /** Optional conditional check to determine if invalidation should proceed */
  shouldInvalidate?: (data: T) => boolean;
}

/**
 * Hook to manage socket event listeners with standard side effects like RTK Query invalidation and toasts.
 *
 * @param nameSpace - The socket namespace to connect to (e.g., "/")
 * @param configs - Configuration for events and their respective side effects
 * @returns Object containing the socket instance and connection status
 */
export const useSocketEvents = <T extends SocketData = SocketData>(
  nameSpace: SocketNamespaces,
  configs: SocketEventConfig<T>[],
): ReturnType<typeof useSocket> => {
  const { socket, isConnected } = useSocket(nameSpace);
  const dispatch = useDispatch();

  // Use a ref to store the latest configs to prevent unnecessary re-bindings
  // when configs are defined inline in components.
  const configsRef = useRef(configs);

  useEffect(() => {
    configsRef.current = configs;
  }, [configs]);

  useEffect(() => {
    if (!socket) {
      return;
    }

    const handlers: {
      eventName: string;
      handler: (data: SocketData) => void;
    }[] = [];

    // Setup handlers based on current configs
    configsRef.current.forEach((initialConfig) => {
      const eventNames = Array.isArray(initialConfig.events)
        ? initialConfig.events
        : [initialConfig.events];

      eventNames.forEach((eventName) => {
        const handler = (passedData: SocketData): void => {
          const data = passedData as T;
          const config = initialConfig;

          // Check conditional logic if provided
          if (config.shouldInvalidate && !config.shouldInvalidate(data)) {
            return;
          }

          if (config.invalidations) {
            // const uniqueInvalidations = new Map();
            config.invalidations.forEach(({ api, tags, moduleName }) => {
              // console.log({ api });
              // const key = `${api.reducerPath}-${JSON.stringify(tags)}`;
              // if (!uniqueInvalidations.has(key)) {
              //   uniqueInvalidations.set(key, { api, tags, moduleName });
              // }

              if (moduleName) {
                // handleSocketUpdate takes care of multi-tab sync and standard toast
                handleSocketUpdate({
                  socketData: data,
                  dispatch,
                  api,
                  tags,
                  moduleName,
                });
              } else {
                dispatch(api.util.invalidateTags(tags));
              }
            });
          }

          // Display toast if provided and not already handled by handleSocketUpdate
          // (handleSocketUpdate shows its own toast if from a different user)
          if (config.toastMessage) {
            const shouldShowToast =
              !config.invalidations ||
              config.invalidations?.some((inv) => !inv.moduleName);

            // Only show custom toast if handleSocketUpdate wasn't used or if we specifically want it
            // (Note: handleSocketUpdate only shows toast if from a different user)
            if (shouldShowToast) {
              const message =
                typeof config.toastMessage === "function"
                  ? config.toastMessage(data)
                  : config.toastMessage;
              tableUpdateToast(data, message);
            }
          }

          if (config.onEvent) {
            config.onEvent(data);
          }
        };

        socket.on(eventName, handler);
        handlers.push({ eventName, handler });
      });
    });

    // eslint-disable-next-line consistent-return
    return (): void => {
      handlers.forEach(({ eventName, handler }) => {
        socket.off(eventName, handler);
      });
    };
  }, [socket, dispatch]);

  return { socket, isConnected };
};
